import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Film, ImagePlus, Loader2, Mail, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm";

const Attachments = ({ base, list, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`${base}/attachments`, fd); } catch (e) { toast.error(`${f.name} : ${formatApiError(e)}`); }
    }
    setBusy(false); onChange();
  };
  const remove = async (fid) => { try { await api.delete(`${base}/attachments/${fid}`); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="space-y-2" data-testid="claim-attachments">
      <div className="flex flex-wrap gap-1.5">{list.map((f) => (
        <span key={f.file_id} className="flex items-center gap-1 rounded-full bg-muted py-1 pl-2.5 pr-1 text-xs" data-testid={`claim-attachment-${f.file_id}`}>
          {f.content_type.startsWith("video") ? <Film className="h-3.5 w-3.5" /> : <ImagePlus className="h-3.5 w-3.5" />}<span className="max-w-[10rem] truncate">{f.name}</span>
          <button type="button" onClick={() => remove(f.file_id)} className="rounded-full p-0.5 hover:bg-background" aria-label="Retirer" data-testid={`claim-attachment-remove-${f.file_id}`}><X className="h-3 w-3" /></button></span>))}</div>
      <input ref={ref} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid="claim-attachment-input" />
      <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => ref.current?.click()} data-testid="claim-attachment-add">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1 h-4 w-4" />} Ajouter photos / vidéo</Button>
      <p className="text-[11px] text-muted-foreground">Photos 10 Mo max, vidéo 50 Mo max, 12 fichiers au total.</p>
    </div>
  );
};

export const ClaimEmailDialog = ({ hid, claim, onOpenChange }) => {
  const qc = useQueryClient();
  const base = `/households/${hid}/claims/${claim.id}`;
  const [t, setT] = useState(null);
  const [v, setV] = useState({ to: "", subject: "", body: "" });
  const load = async (keep) => {
    const { data } = await api.get(`${base}/email`);
    setT(data);
    setV((s) => (keep ? s : { to: data.to, subject: data.subject, body: data.body }));
  };
  useEffect(() => { load(false).catch((e) => toast.error(formatApiError(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAtt = async () => { await load(true); qc.invalidateQueries({ queryKey: ["insurance"] }); };
  const send = async () => {
    try { const { data } = await api.post(`${base}/email/send`, v); toast.success(data.simulated ? "Envoi simulé (compte démo) et ajouté au suivi" : "Déclaration envoyée à l'assureur"); qc.invalidateQueries({ queryKey: ["insurance"] }); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const mailto = `mailto:${encodeURIComponent(v.to)}?subject=${encodeURIComponent(v.subject)}&body=${encodeURIComponent(v.body)}`;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="claim-email-dialog">
        <DialogHeader><DialogTitle>Email de déclaration · {claim.ref}</DialogTitle>
          <DialogDescription>Pré-rempli avec votre contrat, le bien et les garanties. Relisez, ajoutez vos photos ou une vidéo, puis envoyez.</DialogDescription></DialogHeader>
        {!t ? <div className="h-40 animate-pulse rounded-xl bg-muted" /> : (
          <div className="space-y-3">
            <Input type="email" value={v.to} onChange={(e) => setV({ ...v, to: e.target.value })} placeholder={`Email sinistres de ${t.org_name}`} data-testid="claim-email-to" />
            <Input value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} data-testid="claim-email-subject" />
            <Textarea value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} rows={14} className="font-mono text-xs" data-testid="claim-email-body" />
            <Attachments base={base} list={t.attachments} onChange={refreshAtt} />
            {t.emails.length > 0 && <p className="text-xs text-muted-foreground" data-testid="claim-email-history">Déjà envoyé {t.emails.length} fois · dernier le {new Date(t.emails[t.emails.length - 1].sent_at).toLocaleDateString("fr-FR")}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button className="rounded-full" disabled={!v.to} onClick={send} data-testid="claim-email-send"><Send className="mr-1 h-4 w-4" /> Envoyer avec CASHMY</Button>
              <Button variant="outline" className="rounded-full" asChild><a href={mailto} data-testid="claim-email-mailto"><Mail className="mr-1 h-4 w-4" /> Ouvrir dans ma messagerie</a></Button>
              <Button variant="ghost" className="rounded-full" onClick={() => navigator.clipboard.writeText(`${v.subject}\n\n${v.body}`).then(() => toast.success("Texte copié"))} data-testid="claim-email-copy"><Copy className="mr-1 h-4 w-4" /> Copier</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Avec CASHMY, les photos et la vidéo sont jointes sous forme de liens sécurisés et l'envoi est ajouté au suivi du sinistre.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
